import { useAppDispatch } from 'apps/placeme/src/redux/hooks'
import React, { ReactNode, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSignal } from '@preact/signals-react'
import { CoinsButton, Loader } from '@dataplace.ai/ui-components/atoms'
import { placemePlans } from '@dataplace.ai/constants'
import { useOverviewData } from '../hooks/useOverviewData'
import { fetchOverviewDataAction, fetchTileDataAction } from '../../../slice/analysisSlice'
import { OverlayMessage, OverlayTopWrapper, StyledOverlay } from './OverviewTile.styles'
import { OVERVIEW_COST } from '../constants'

export const Overlay = ({ children }: {children: ReactNode[] | ReactNode}): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const {
    dataLoaded, subscriptionId, subscriptionLoaded, hasUnlimitedCredits,
    reportId, currentSubscriptionData, isTrialOrWhite, hasEnoughCredits,
    overviewError,
  } = useOverviewData()
  const dataRequested = useSignal(false)

  const requestOverviewData = useCallback(() => {
    dataRequested.value = true
    dispatch(fetchOverviewDataAction({
      subscriptionId,
      reportId,
    }))
  }, [subscriptionId, dispatch, fetchTileDataAction])

  useEffect(() => {
    if ((!hasUnlimitedCredits && !isTrialOrWhite) || dataLoaded
      || !subscriptionLoaded || dataRequested.value || !hasEnoughCredits) return
    requestOverviewData()
  }, [hasUnlimitedCredits, dataLoaded, dataRequested, subscriptionLoaded, isTrialOrWhite, hasEnoughCredits])

  if (!dataLoaded && (dataRequested.value || !subscriptionLoaded)) {
    return (
      <OverlayTopWrapper>
        <Loader />
      </OverlayTopWrapper>
    )
  }

  if (overviewError || (!dataLoaded && !hasEnoughCredits)) {
    return (
      <>
        <OverlayTopWrapper>
          <OverlayMessage>{t(overviewError ?? 'status.error.stripe.subscription.not_enough_credits')}</OverlayMessage>
        </OverlayTopWrapper>
        <StyledOverlay>
          {children}
        </StyledOverlay>
      </>
    )
  }

  return (subscriptionLoaded && (!hasUnlimitedCredits && !isTrialOrWhite) && !dataLoaded
    ? (
      <>
        {currentSubscriptionData?.value?.planExact !== placemePlans.white && (
          <OverlayTopWrapper>
            <CoinsButton
              disabled={!hasEnoughCredits}
              onClick={requestOverviewData}
              text={t('placeme.overview.add_overview_button')}
              value={OVERVIEW_COST}
            />
          </OverlayTopWrapper>
        )}
        <StyledOverlay>
          {children}
        </StyledOverlay>
      </>
    )
    : <>{children}</>
  )
}
